import { querySelectorDeep } from "omni-ui";

export const TableUtils = {
    placementFlattendedDataCache: new Map(),
    placementCheckAll: 'none',

    campaignOnlineFlattendedDataCache: new Map(),
    campaignOnlineTableCheckAll: 'none',
    campaignPrintFlattendedDataCache: new Map(),
    campaignPrintTableCheckAll: 'none',
    unmappedFlattendedDataCache: new Map(),
    unmappedTableCheckAll: 'none',

    /**
     * Flattens a nested data structure into a single array.
     * @param {Array} data - The nested data array to flatten.
     * @returns {Array} The flattened data array.
     */
    flattenData: (data) => {
        const flattened = [];
        const walk = levelData => {
            for (const row of levelData) {
                //@ts-ignore
                flattened.push(row);
                if (row.nestedData) {
                    walk(row.nestedData);
                }
            }
        };
        walk(data);
        return flattened;
    },

    /**
     * Checks or unchecks parent rows based on the current row's checked status.
     * @param {Array} nestedData - The nested data array.
     * @param {Object} currRow - The current row being checked or unchecked.
     */
    checkAndUncheckParent: (nestedData, currRow) => {
        const flatData = TableUtils.flattenData(nestedData);
        const childRows = flatData.filter(data => Number(data.parent_id) === Number(currRow.id));
        let checkChild = (rows) => {
            if (rows?.length) {
                rows.forEach(childRow => {
                    childRow.checked = currRow.checked;
                    const nestedChildRows = flatData.filter(data => Number(data.parent_id) === Number(childRow.id))
                    checkChild(nestedChildRows);
                })
            }
        }
        checkChild(childRows);
    },

    /**
    * Updates the checked status of parent and grandparent rows based on the checked status of the current row's siblings.
    * @param {Array} nestedData - The nested data array.
    * @param {Object} currRow - The current row object.
    */
    checkAndUncheckChildren: (nestedData, currRow) => {
        if (!currRow?.parent_id) return;

        const flatData = TableUtils.flattenData(nestedData);
        const parentRow = TableUtils.findRowById(flatData, currRow.parent_id);
        if (!parentRow) return;

        TableUtils.updateParentRowStatus(flatData, parentRow);
    },

    /**
     * Finds a row by its ID.
     * @param {Array} flatData - The flattened data array.
     * @param {number} id - The ID of the row to find.
     * @returns {Object} - The found row object.
     */
    findRowById: (flatData, id) => {
        return flatData.find(data => Number(data.id) === Number(id));
    },

    /**
     * Updates the checked status of the parent row based on its siblings' statuses.
     * @param {Array} flatData - The flattened data array.
     * @param {Object} parentRow - The parent row object.
     */
    updateParentRowStatus: (flatData, parentRow) => {
        const siblings = TableUtils.findSiblings(flatData, parentRow.id);
        const checkedChildRows = siblings.filter(data => data?.checked === 'all');

        if (siblings.length === checkedChildRows.length) {
            parentRow.checked = 'all';
        } else if (checkedChildRows.length) {
            parentRow.checked = 'some';
        } else {
            parentRow.checked = 'none';
        }

        if (parentRow?.parent_id) {
            TableUtils.updateGrandParentRowStatus(flatData, parentRow);
        }
    },

    /**
     * Finds the siblings of a row.
     * @param {Array} flatData - The flattened data array.
     * @param {number} parentId - The parent ID of the siblings to find.
     * @returns {Array} - The array of sibling rows.
     */
    findSiblings: (flatData, parentId) => {
        return flatData.filter(data => Number(data.parent_id) === Number(parentId));
    },

    /**
     * Updates the checked status of the grandparent row based on its siblings' statuses.
     * @param {Array} flatData - The flattened data array.
     * @param {Object} parentRow - The parent row object.
     */
    updateGrandParentRowStatus: (flatData, parentRow) => {
        const grandParentRow = TableUtils.findRowById(flatData, parentRow.parent_id);
        if (!grandParentRow) return;

        const placementSiblings = TableUtils.findSiblings(flatData, grandParentRow.id);
        const checkedPlacementSiblings = placementSiblings.filter(data => data?.checked === parentRow.checked);

        if (placementSiblings.length === checkedPlacementSiblings.length) {
            grandParentRow.checked = parentRow.checked;
        } else {
            grandParentRow.checked = 'some';
        }
        // Check for granparent row's parent and update the checkbox status
        if (grandParentRow?.parent_id) {
            TableUtils.updateGrandParentRowStatus(flatData, grandParentRow);
        }
    },

    /**
     * Calculates the total number of checked and indeterminate rows.
     * @param {Object} row - The current row being processed.
     * @param {Object} currRow - The row that triggered the check.
     */
    getTotalCheckedAndIndeterminate(row, currRow, tableId) {
        if (!row?.nestedData) return;
        let checkedSum = 0;
        let indeterminateSum = 0;
        if (row._uniqueKey === currRow._uniqueKey) {
            let flat;
            if (tableId === 'placement-table') {
                if (TableUtils.placementFlattendedDataCache?.has(row)) {
                    flat = TableUtils.placementFlattendedDataCache?.get(row);
                } else {
                    flat = TableUtils.flattenData(row.nestedData);
                    TableUtils.placementFlattendedDataCache?.set(row, flat);
                }
            } else if (tableId === 'online-table') { // campaign details online table in select channels
                if (TableUtils.campaignOnlineFlattendedDataCache?.has(row)) {
                    flat = TableUtils.campaignOnlineFlattendedDataCache?.get(row);
                } else {
                    flat = TableUtils.flattenData(row.nestedData);
                    TableUtils.campaignOnlineFlattendedDataCache?.set(row, flat);
                }
            } else if (tableId === 'print-table') { // campaign details online table in select channels
                if (TableUtils.campaignPrintFlattendedDataCache?.has(row)) {
                    flat = TableUtils.campaignPrintFlattendedDataCache?.get(row);
                } else {
                    flat = TableUtils.flattenData(row.nestedData);
                    TableUtils.campaignPrintFlattendedDataCache?.set(row, flat);
                }
            }  else if (tableId === 'orphaned-delivered-table') {
                if (TableUtils.unmappedFlattendedDataCache?.has(row)) {
                    flat = TableUtils.unmappedFlattendedDataCache?.get(row);
                } else {
                    flat = TableUtils.flattenData(row.nestedData);
                    TableUtils.unmappedFlattendedDataCache?.set(row, flat);
                }
            }
            row.checked = currRow.checked;
            flat.forEach(data => {
                data.checked = currRow.checked;
            });
            return;
        } else if (row._uniqueKey !== currRow._uniqueKey) {
            row.nestedData.forEach(nestedRow => {
                TableUtils.getTotalCheckedAndIndeterminate(nestedRow, currRow, tableId);
                if (nestedRow.checked === 'all') {
                    checkedSum++;
                } else if (nestedRow.checked === 'some') {
                    indeterminateSum++;
                }
            });
            if (checkedSum === row.nestedData.length) row.checked = 'all';
            else if ((checkedSum > 0 && checkedSum < row.nestedData.length) || indeterminateSum > 0) row.checked = 'some';
            else row.checked = 'none';
        }
    },

    /**
     * Checks the select all checkbox based on the nested data.
     * @param {Array} nestedData - The nested data array.
     * @param {string} tableId - The ID of the table element.
     */
    checkSelectAll: (nestedData, selectAllCheckboxId) => {        
        let checkedSum = 0;
        let indeterminateSum = 0;
        nestedData.forEach(row => {
            if (row.checked === 'all') {
                checkedSum++;
            } else if (row.checked === 'some') {
                indeterminateSum++;
            }
        });

        if (selectAllCheckboxId === 'placements-table-selectall') {
            if (checkedSum === nestedData.length) {
                TableUtils.placementCheckAll = 'all';
            } else if ((checkedSum > 0 && checkedSum < nestedData.length) || indeterminateSum > 0) {
                TableUtils.placementCheckAll = 'some';
            } else {
                TableUtils.placementCheckAll = 'none';
            }
            const checkAllRef = querySelectorDeep('omni-table', 'left-table')?.shadowRoot.getElementById(selectAllCheckboxId);
            
            if(checkAllRef){
                checkAllRef.checked = TableUtils.placementCheckAll === 'all';
                checkAllRef.indeterminate = TableUtils.placementCheckAll === 'some';
            }
            // Check for sticky header element & update the select all checkbox
            let placementHeaderEl = document.getElementById('placement-table')?.shadowRoot?.querySelector('.sticky-header-container');
            if (placementHeaderEl) {
                let selectAllCheckboxEl = document.getElementById('placement-table')?.shadowRoot?.querySelector('#placements-table-selectall');
                selectAllCheckboxEl.checked = TableUtils.placementCheckAll === 'all';
                selectAllCheckboxEl.indeterminate = TableUtils.placementCheckAll === 'some';
            }
        } else if (selectAllCheckboxId === 'campaign-online-table-selectall') {
            if (checkedSum === nestedData.length) {
                TableUtils.campaignOnlineTableCheckAll = 'all';
            } else if ((checkedSum > 0 && checkedSum < nestedData.length) || indeterminateSum > 0) {
                TableUtils.campaignOnlineTableCheckAll = 'some';
            } else {
                TableUtils.campaignOnlineTableCheckAll = 'none';
            }
            const checkAllRef = querySelectorDeep('omni-table')?.shadowRoot.getElementById(selectAllCheckboxId);
            if(checkAllRef){
                checkAllRef.checked = TableUtils.campaignOnlineTableCheckAll === 'all';
                checkAllRef.indeterminate = TableUtils.campaignOnlineTableCheckAll === 'some';
            }
        } else if (selectAllCheckboxId === 'campaign-print-table-selectall') {
            if (checkedSum === nestedData.length) {
                TableUtils.campaignPrintTableCheckAll = 'all';
            } else if ((checkedSum > 0 && checkedSum < nestedData.length) || indeterminateSum > 0) {
                TableUtils.campaignPrintTableCheckAll = 'some';
            } else {
                TableUtils.campaignPrintTableCheckAll = 'none';
            }
            const checkAllRef = querySelectorDeep('omni-table')?.shadowRoot.getElementById(selectAllCheckboxId);
            if(checkAllRef){
                checkAllRef.checked = TableUtils.campaignPrintTableCheckAll === 'all';
                checkAllRef.indeterminate = TableUtils.campaignPrintTableCheckAll === 'some';
            }
        } else if (selectAllCheckboxId === 'orphaned-delivered-table-selectall') {
            if (checkedSum === nestedData.length) {
                TableUtils.unmappedTableCheckAll = 'all';
            } else if ((checkedSum > 0 && checkedSum < nestedData.length) || indeterminateSum > 0) {
                TableUtils.unmappedTableCheckAll = 'some';
            } else {
                TableUtils.unmappedTableCheckAll = 'none';
            }
            const checkAllRef = querySelectorDeep('omni-table')?.shadowRoot.getElementById(selectAllCheckboxId);
            if(checkAllRef){
                checkAllRef.checked = TableUtils.unmappedTableCheckAll === 'all';
                checkAllRef.indeterminate = TableUtils.unmappedTableCheckAll === 'some';
            }
        }
    },

    /**
     * Handles the check all functionality for a table.
     * @param {Array} nestedData - The nested data array.
     * @param {string} tableId - The ID of the table element.
     */
    handleCheckAll: (nestedData, tableId) => {

        if (tableId === 'placement-table') {
            let flat;
            if (TableUtils.placementFlattendedDataCache.has('all')) {
                flat = TableUtils.placementFlattendedDataCache.get('all');
            } else {
                flat = TableUtils.flattenData(nestedData);
                TableUtils.placementFlattendedDataCache.set('all', flat);
            }
            flat.forEach(data => {
                data.checked = TableUtils.placementCheckAll;
            });
        } else if (tableId === 'online-table') {
            let flat;
            if (TableUtils.campaignOnlineFlattendedDataCache.has('all')) {
                flat = TableUtils.campaignOnlineFlattendedDataCache.get('all');
            } else {
                flat = TableUtils.flattenData(nestedData);
                TableUtils.campaignOnlineFlattendedDataCache.set('all', flat);
            }
            flat.forEach(data => {
                data.checked = TableUtils.campaignOnlineTableCheckAll;
            });
        } else if (tableId === 'print-table') {
            let flat;
            if (TableUtils.campaignPrintFlattendedDataCache.has('all')) {
                flat = TableUtils.campaignPrintFlattendedDataCache.get('all');
            } else {
                flat = TableUtils.flattenData(nestedData);
                TableUtils.campaignPrintFlattendedDataCache.set('all', flat);
            }
            flat.forEach(data => {
                data.checked = TableUtils.campaignPrintTableCheckAll;
            });
        } else if (tableId === 'orphaned-delivered-table') {
            let flat;
            if (TableUtils.unmappedFlattendedDataCache.has('all')) {
                flat = TableUtils.unmappedFlattendedDataCache.get('all');
            } else {
                flat = TableUtils.flattenData(nestedData);
                TableUtils.unmappedFlattendedDataCache.set('all', flat);
            }
            flat.forEach(data => {
                data.checked = TableUtils.unmappedTableCheckAll;
            });
        }

        const tableRef = document.getElementById(tableId);
        tableRef?.requestUpdate();
    },

    /**
     * Traverses nodes for checkbox updates.
     * @param {number} parentIdx - The index of the parent row.
     * @param {Object} currRow - The current row being processed.
     * @param {Array} nestedData - The nested data array.
     * @param {string} selectAllCheckboxId - The ID of the select all checkbox element.
     * @param {string} tableId - The ID of the table element.
     */
    traverseNodeForCheckbox: (parentIdx, currRow, nestedData, selectAllCheckboxId, tableId) => {                
        TableUtils.checkAndUncheckChildren(nestedData, currRow);
        TableUtils.checkAndUncheckParent(nestedData, currRow);
        TableUtils.getTotalCheckedAndIndeterminate(nestedData[parentIdx], currRow, tableId);
        TableUtils.checkSelectAll(nestedData, selectAllCheckboxId);
        const tableRef = document.getElementById(tableId);
        tableRef?.requestUpdate();
    },

    /**
     * Below code is used to update the checked status of parent rows based on the child rows checked status
     * @param {Array} data - The nested data array.
     * @returns {Array} - The updated data array.
     */
    checkPublisherAndChannels : (data) => {
        return data.reduce((acc, current) => {
            const getCheckedValue = (arrayData) => {
                return TableUtils.placementCheckAll === 'all' ? 'all' : arrayData.every(item => item.checked === 'all') ? 'all' : arrayData.some(item => item.checked === 'all' || item.checked === 'some') ? 'some' : 'none';
            }
            
            if (current.nestedData?.length) {
                current.nestedData = current.nestedData.map((item) => {
                    if(item?.nestedData?.length) {
                        item.checked = getCheckedValue(item?.nestedData);
                    }
                    return item;
                })
                current.checked = getCheckedValue(current?.nestedData)
            }
                acc.push(current);
                return acc;
        }, [])
    }
}